/**
 * BottomNav Component
 *
 * Main bottom navigation with club-first navigation model.
 * Layout: Club · Activity | O | Discuss · Settings
 * Issue #910: "Home" renamed to "Club" with Users icon
 * 
 * Issue #692: Home tab routes to user's primary club
 * Issue #695: Club-scoped navigation with context resolution
 * Issue #728: Activity tab routes to Activity page (Events/Groups/Volunteer)
 * Related: ADR-0005 Navigation Shell & Menu Architecture
 */

import { useMemo } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { MAIN_NAV_ITEMS, isAdminRoute } from '../config/navigationConfig';
import { getZIndexClass } from '../config/zIndex';
import { useClubNavContext } from '../hooks/useClubNavContext';
import { AdminBottomNav } from './AdminBottomNav';
import type { LucideIcon } from 'lucide-react';

export function BottomNav() {
  const location = useLocation();

  // Resolve club context with memory (Issue #724)
  const clubSlugForNav = useClubNavContext();

  // George's optimization: Memoize static nav item splits (16ms → 8ms)
  const leftItems = useMemo(() =>
    MAIN_NAV_ITEMS.filter(({ id }) => ['home', 'activity'].includes(id)),
    []
  );
  const rightItems = useMemo(() =>
    MAIN_NAV_ITEMS.filter(({ id }) => ['discuss', 'settings'].includes(id)),
    []
  );

  // Issue #1063: Show admin nav on admin routes (after hooks per rules-of-hooks)
  if (isAdminRoute(location.pathname)) {
    return <AdminBottomNav />;
  }

  return (
    <nav 
      className={`fixed bottom-0 left-0 right-0 ${getZIndexClass('bottomNav')} rounded-t-2xl md:hidden`}
      style={{
        backgroundColor: 'var(--oro-surface-alt)',
        borderTop: '1px solid var(--oro-border)',
        boxShadow: 'var(--shadow-floating)',
        padding: '0.75rem 0',
        // Ensure fixed positioning works on iOS Safari
        position: 'fixed',
        willChange: 'transform',
        paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0))',
      }}
    >
      <div className="flex items-center justify-around max-w-md mx-auto">
        {leftItems.map((item) => {
          if (item.id === 'home' && clubSlugForNav) {
            return (
              <HomeNavItem 
                key={item.id} 
                clubSlug={clubSlugForNav}
                icon={item.icon}
                label={item.label}
              />
            );
          }
          if (item.id === 'activity' && clubSlugForNav) {
            return (
              <ActivityNavItem 
                key={item.id} 
                clubSlug={clubSlugForNav}
                icon={item.icon}
                label={item.label}
              />
            );
          }
          return <StaticNavItem key={item.id} {...item} />;
        })}
        
        {/* O slot - inert, purely presentational (Frank's final decision) */}
        <InertOSlot />
        
        {rightItems.map((item) => {
          if (item.id === 'discuss' && clubSlugForNav) {
            return (
              <DiscussNavItem 
                key={item.id} 
                clubSlug={clubSlugForNav}
                icon={item.icon}
                label={item.label}
              />
            );
          }
          return <StaticNavItem key={item.id} {...item} />;
        })}
      </div>
    </nav>
  );
}

/**
 * HomeNavItem
 * Routes to user's club context (Issue #692, #695)
 */
interface NavItemProps {
  clubSlug: string;
  icon: LucideIcon;
  label: string;
}

function HomeNavItem({ clubSlug, icon: Icon, label }: NavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const clubPath = `/clubs/${clubSlug}`;
  const isActive = location.pathname === clubPath || location.pathname === '/home';

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // #778: Scroll to top if tapping current tab
    if (isActive) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(clubPath, { state: { fromHomeTab: true } });
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={{
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      }}
      type="button"
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </button>
  );
}

/**
 * ActivityNavItem
 * Routes to Activity page with Events/Groups/Volunteer tabs (Issue #728)
 */
function ActivityNavItem({ icon: Icon, label }: NavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Route to /activity (global activity page)
  const activityPath = '/activity';
  const isActive = location.pathname.startsWith('/activity');
  const isExactPath = location.pathname === activityPath;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // #778: Scroll to top if tapping current tab (only if exactly on root path)
    // #1197: If on a sub-page, navigate to root path
    if (isExactPath) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(activityPath, { state: { fromActivityTab: true } });
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={{
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      }}
      type="button"
      data-tour="club-activities"
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </button>
  );
}

/**
 * DiscussNavItem
 * Club-scoped chat navigation (Issue #695)
 */
function DiscussNavItem({ clubSlug, icon: Icon, label }: NavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Defensive fallback: no club → ChatLandingPage empty state (#1338)
  const chatPath = clubSlug ? `/clubs/${clubSlug}/chat` : '/chat';

  // George's explicit null handling (prevents "never active" surprises)
  const isActive = !!(clubSlug && location.pathname.startsWith(`/clubs/${clubSlug}/chat`));
  const isExactPath = location.pathname === chatPath;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // #778: Scroll to top if tapping current tab (only if exactly on root path)
    // #1197: If on a sub-page (e.g., chat room), navigate to root chat path
    if (isExactPath) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(chatPath, { state: { fromDiscussTab: true } });
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={{
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      }}
      type="button"
      data-tour="club-chat-entry"
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </button>
  );
}

/**
 * StaticNavItem
 * For non-club-scoped items like Settings (Issue #695)
 */
interface StaticNavItemProps {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
}

function StaticNavItem({ id, path, label, icon: Icon }: StaticNavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = location.pathname.startsWith(path);
  const isExactPath = location.pathname === path;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // #778: Scroll to top if tapping current tab (only if exactly on root path)
    // #1197: If on a sub-page (e.g., /settings/profile), navigate to root path
    if (isExactPath) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    navigate(path);
  };

  return (
    <button
      onClick={handleClick}
      className="flex flex-col items-center justify-center gap-1 py-2 px-3 transition-colors"
      style={{
        color: isActive ? 'var(--oro-gold)' : 'var(--text-muted)'
      }}
      type="button"
      data-tour={id === 'settings' ? 'settings-tab' : undefined}
    >
      <div className="w-6 h-6 flex items-center justify-center">
        <Icon className="w-6 h-6" />
      </div>
      <span className="text-[11px] font-medium">
        {label}
      </span>
    </button>
  );
}

/**
 * InertOSlot
 * Purely decorative brand element in center of nav (Frank's final decision)
 * No interaction, no debug overlay - debug moved to Settings (Issue #702)
 */
function InertOSlot() {
  return (
    <div className="flex items-center justify-center px-4" aria-hidden="true">
      <div className="oro-center-button-breathe w-[52px] h-[52px] flex items-center justify-center">
        <div className="oro-center-button-inner w-[44px] h-[44px] flex items-center justify-center shadow-[var(--shadow-card)]">
          {/* ORO Mark - Circular O Badge */}
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ opacity: 0.85 }}
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="var(--oro-gold)"
              strokeWidth="2.5"
              fill="none"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

